import React, { useEffect, useState } from "react";
import { api, extractError, fetchAllListResults } from "../lib/api";
import { useLookups } from "../context/LookupOptionsContext";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Textarea } from "./ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { toast } from "sonner";
import {
  isValidVehicleNumber,
  sanitizeVehicleNumberInput,
  VEHICLE_NUMBER_FORMAT_MSG,
  VEHICLE_NUMBER_PLACEHOLDER,
} from "../lib/vehicleNumber";

const GATE_PURPOSES = [
  { value: "INWARD_PADDY", label: "Inward — Paddy" },
  { value: "INWARD_STORE", label: "Inward — Store" },
  { value: "OUTWARD_RICE", label: "Outward — Rice" },
  { value: "VISITOR", label: "Visitor" },
];

export default function AdminQuickEditDialog({
  entityType,
  entityId,
  entityLabel,
  open,
  onOpenChange,
  onSaved,
}) {
  const { optionsFor } = useLookups();
  const varieties = optionsFor("paddy_variety");
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState(null);
  const [parties, setParties] = useState([]);

  useEffect(() => {
    if (!open || !entityId) {
      setForm(null);
      return;
    }
    setLoading(true);
    const load = async () => {
      try {
        if (entityType === "gate_pass") {
          const [detail, partyRows] = await Promise.all([
            api.get(`/gate-passes/${entityId}`),
            fetchAllListResults("/parties"),
          ]);
          const gp = detail.data.gate_pass;
          setParties(partyRows);
          setForm({
            vehicle_number: sanitizeVehicleNumberInput(gp.vehicle_number || ""),
            driver_name: gp.driver_name || "",
            driver_phone: gp.driver_phone || "",
            purpose: gp.purpose || "INWARD_PADDY",
            party_id: gp.party_id ? String(gp.party_id) : "",
            party_name: gp.party_name || "",
            expected_variety: gp.expected_variety || "1509",
            expected_bags: gp.expected_bags ?? "",
            notes: gp.notes || "",
          });
        } else if (entityType === "stack") {
          const { data: s } = await api.get(`/stacks/${entityId}`);
          setForm({
            variety: s.variety || "1509",
            capacity_qtl: s.capacity_qtl ?? 1000,
            current_qtl: s.current_qtl ?? 0,
            age_days: s.age_days ?? 0,
          });
        } else if (entityType === "production_yield") {
          const { data: y } = await api.get(`/production/${entityId}`);
          setForm({
            date: y.date || "",
            input_variety: y.input_variety || "1509",
            input_qtl: y.input_qtl ?? 0,
            head_rice_qtl: y.head_rice_qtl ?? 0,
            nakku_qtl: y.nakku_qtl ?? 0,
            bran_qtl: y.bran_qtl ?? 0,
            husk_qtl: y.husk_qtl ?? 0,
            dust_qtl: y.dust_qtl ?? 0,
            bags_packed: y.bags_packed ?? 0,
          });
        }
      } catch (err) {
        toast.error(extractError(err));
        onOpenChange(false);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [open, entityId, entityType, onOpenChange]);

  const save = async (e) => {
    e.preventDefault();
    if (!form) return;
    setSubmitting(true);
    try {
      if (entityType === "gate_pass") {
        const vehicle = sanitizeVehicleNumberInput(form.vehicle_number);
        if (!isValidVehicleNumber(vehicle)) {
          toast.error(VEHICLE_NUMBER_FORMAT_MSG);
          setSubmitting(false);
          return;
        }
        const payload = {
          vehicle_number: vehicle,
          driver_name: form.driver_name.trim(),
          driver_phone: form.driver_phone?.trim() || null,
          purpose: form.purpose,
          expected_variety: form.expected_variety || null,
          expected_bags: form.expected_bags !== "" ? Number(form.expected_bags) : null,
          notes: form.notes?.trim() || null,
        };
        if (form.party_id) {
          payload.party_id = Number(form.party_id);
        } else {
          payload.party_id = null;
          payload.party_name = form.party_name?.trim() || null;
        }
        await api.patch(`/gate-passes/${entityId}`, payload);
      } else if (entityType === "stack") {
        await api.patch(`/stacks/${entityId}`, {
          variety: form.variety,
          capacity_qtl: Number(form.capacity_qtl),
          current_qtl: Number(form.current_qtl),
          age_days: Number(form.age_days),
        });
      } else if (entityType === "production_yield") {
        await api.patch(`/production/${entityId}`, {
          date: form.date,
          input_variety: form.input_variety,
          input_qtl: Number(form.input_qtl),
          head_rice_qtl: Number(form.head_rice_qtl),
          nakku_qtl: Number(form.nakku_qtl),
          bran_qtl: Number(form.bran_qtl),
          husk_qtl: Number(form.husk_qtl),
          dust_qtl: Number(form.dust_qtl),
          bags_packed: Number(form.bags_packed),
        });
      }
      toast.success("Updated");
      onOpenChange(false);
      onSaved?.();
    } catch (err) {
      toast.error(extractError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const title =
    entityType === "gate_pass"
      ? "Edit gate pass"
      : entityType === "stack"
        ? "Edit stack"
        : "Edit production yield";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid={`admin-edit-${entityType}-${entityId}`}>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>
            Administrator update for <strong>{entityLabel || entityId}</strong>
          </DialogDescription>
        </DialogHeader>

        {loading || !form ? (
          <p className="text-sm text-muted-foreground py-6 text-center">Loading…</p>
        ) : (
          <form onSubmit={save} className="space-y-4">
            {entityType === "gate_pass" && (
              <>
                <Field label="Vehicle number">
                  <Input
                    value={form.vehicle_number}
                    onChange={(e) =>
                      setForm({ ...form, vehicle_number: sanitizeVehicleNumberInput(e.target.value) })
                    }
                    placeholder={VEHICLE_NUMBER_PLACEHOLDER}
                    required
                    autoCapitalize="characters"
                    spellCheck={false}
                  />
                  <p className="text-[10px] text-muted-foreground mt-1">Letters and digits only — no spaces or symbols.</p>
                </Field>
                <Field label="Driver name">
                  <Input value={form.driver_name} onChange={(e) => setForm({ ...form, driver_name: e.target.value })} required />
                </Field>
                <Field label="Driver phone">
                  <Input value={form.driver_phone} onChange={(e) => setForm({ ...form, driver_phone: e.target.value })} />
                </Field>
                <Field label="Purpose">
                  <Select value={form.purpose} onValueChange={(v) => setForm({ ...form, purpose: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {GATE_PURPOSES.map((p) => (
                        <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>
                <Field label="Party">
                  <Select value={form.party_id || "__none__"} onValueChange={(v) => setForm({ ...form, party_id: v === "__none__" ? "" : v })}>
                    <SelectTrigger><SelectValue placeholder="Optional party" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">— None —</SelectItem>
                      {parties.map((p) => (
                        <SelectItem key={p.id} value={String(p.id)}>{p.display_name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Expected variety">
                    <Select value={form.expected_variety} onValueChange={(v) => setForm({ ...form, expected_variety: v })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {varieties.map((v) => <SelectItem key={v.value} value={v.value}>{v.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </Field>
                  <Field label="Expected bags">
                    <Input type="number" value={form.expected_bags} onChange={(e) => setForm({ ...form, expected_bags: e.target.value })} />
                  </Field>
                </div>
                <Field label="Notes">
                  <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} />
                </Field>
              </>
            )}

            {entityType === "stack" && (
              <>
                <Field label="Variety">
                  <Select value={form.variety} onValueChange={(v) => setForm({ ...form, variety: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {varieties.map((v) => <SelectItem key={v.value} value={v.value}>{v.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </Field>
                <Field label="Capacity (qtl)">
                  <Input type="number" step="0.1" value={form.capacity_qtl} onChange={(e) => setForm({ ...form, capacity_qtl: e.target.value })} required />
                </Field>
                <Field label="Current stock (qtl)">
                  <Input type="number" step="0.1" value={form.current_qtl} onChange={(e) => setForm({ ...form, current_qtl: e.target.value })} />
                </Field>
                <Field label="Age (days)">
                  <Input type="number" value={form.age_days} onChange={(e) => setForm({ ...form, age_days: e.target.value })} />
                </Field>
              </>
            )}

            {entityType === "production_yield" && (
              <>
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Date">
                    <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} required />
                  </Field>
                  <Field label="Input variety">
                    <Select value={form.input_variety} onValueChange={(v) => setForm({ ...form, input_variety: v })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {varieties.map((v) => <SelectItem key={v.value} value={v.value}>{v.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </Field>
                </div>
                <Field label="Input (qtl)">
                  <Input type="number" step="0.1" value={form.input_qtl} onChange={(e) => setForm({ ...form, input_qtl: e.target.value })} />
                </Field>
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Head rice (qtl)"><Input type="number" step="0.1" value={form.head_rice_qtl} onChange={(e) => setForm({ ...form, head_rice_qtl: e.target.value })} /></Field>
                  <Field label="Nakku (qtl)"><Input type="number" step="0.1" value={form.nakku_qtl} onChange={(e) => setForm({ ...form, nakku_qtl: e.target.value })} /></Field>
                  <Field label="Bran (qtl)"><Input type="number" step="0.1" value={form.bran_qtl} onChange={(e) => setForm({ ...form, bran_qtl: e.target.value })} /></Field>
                  <Field label="Husk (qtl)"><Input type="number" step="0.1" value={form.husk_qtl} onChange={(e) => setForm({ ...form, husk_qtl: e.target.value })} /></Field>
                  <Field label="Dust (qtl)"><Input type="number" step="0.1" value={form.dust_qtl} onChange={(e) => setForm({ ...form, dust_qtl: e.target.value })} /></Field>
                  <Field label="Bags packed"><Input type="number" value={form.bags_packed} onChange={(e) => setForm({ ...form, bags_packed: e.target.value })} /></Field>
                </div>
              </>
            )}

            <DialogFooter className="gap-2 sm:gap-0">
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>
                Cancel
              </Button>
              <Button type="submit" disabled={submitting} className="bg-bansal-sage hover:bg-bansal-sageDark text-white">
                {submitting ? "Saving…" : "Save changes"}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">{label}</Label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}
